import {
	Dialog,
	NoResults,
	TabsNavigation,
	TabsNavigationItemData
} from "client/Components";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import {
	AlertManager,
	createRemoteDataSource,
	LazyLoadCursorKind,
	NumberFormatter,
	RemoteDataSourceState,
	useAlerts,
	useForceRerender,
	useOpenDialog,
	useResizeObserver
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { reactionIconMap } from "../ReactionsCountButton/ReactionsCountButton";
import {
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import { reactionNameMap, List } from "./List";
import styles from "./ReactionsDialog.module.less";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";

const PAGE_SIZE = 50;
const ESTIMATED_ROW_HEIGHT = 56;

export type GetReactionsCallback = (
	id: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
) => Promise<LazyLoadPageResult<ReactionUserModel>>;

type GetReactionCountCallback = (
	id: number,
	signal?: AbortSignal
) => Promise<readonly ReactionCounterModel[]>;

type ReactionsDialogProps = {
	itemId: number;
	onClose(): void;
	getReactions: GetReactionsCallback;
	getReactionCounts: GetReactionCountCallback;
};

export const ReactionsDialog = (props: ReactionsDialogProps): ReactElement => {
	const {
		itemId,
		onClose,
		getReactions,
		getReactionCounts
	} = props;

	const [activeReactionTab, setActiveReactionTab] = useState<ReactionKind>();
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [reactionCounts, setReactionCounts] = useState<readonly ReactionCounterModel[]>([]);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [scrollTop, setScrollTop] = useState<number>(0);

	const [forceReload, itemsVersion] = useForceRerender();
	const alertManager: AlertManager = useAlerts();
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const { height } = useResizeObserver(containerRef);
	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		hasLazyLoadError,
		retryToLoadLastPage
	} = useReactionsDataSource({
		filtering: {
			itemId,
			kind: activeReactionTab
		},
		desiredAmountOfItems,
		globalContext: {
			itemsVersion,
			getReactions
		},
	});

	useEffect(
		() => {
			async function fetchCounters (signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const result: readonly ReactionCounterModel[] = await getReactionCounts(itemId, signal);
					setReactionCounts(result);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchCounters(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			getReactionCounts,
			itemId,
			itemsVersion
		]
	);

	const reactionTabs = useMemo(
		(): readonly TabsNavigationItemData<ReactionKind | undefined>[] => convertToReactionTabs(
			reactionCounts,
			statisticsFormatter
		),
		[reactionCounts, statisticsFormatter]
	);

	const loadingState: RemoteDataSourceState = getLoadingState();

	useEffect(
		() => {
			if (!containerRef.current) {
				return;
			}

			const scrollHeight: number = containerRef.current.scrollHeight;
			const scrollableArea: number = scrollHeight - height;

			const areAllPreviouslyRequestedItemsLoaded: boolean = currentItems.length >= desiredAmountOfItems;
			if (scrollTop > scrollableArea - 2 * ESTIMATED_ROW_HEIGHT
				&& areAllPreviouslyRequestedItemsLoaded
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(desiredAmountOfItems + PAGE_SIZE);
			}
		},
		[
			currentItems.length,
			desiredAmountOfItems,
			hasLazyLoadError,
			hasMoreOnServer,
			height,
			scrollTop
		]
	);

	const scrollHandler = useCallback(
		(event: UIEvent<HTMLDivElement>): void => {
			setScrollTop(event.currentTarget.scrollTop);
		},
		[]
	);

	const renderItems = (): ReactElement | null => {
		if (currentItems.length > 0) {
			return (
				<>
					<List
						items={currentItems}
					/>
					{hasLazyLoadError && (
						<NoResults
							trackingId="has-lazy-loading-error"
							icon="gear"
							title={localize("_NotAbleToRetrieveReactions")}
							message={localize("_WeWereUnableToRetrieveReactions")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		switch (loadingState) {
			case "loading":
				return null;
			case "error":
				return (
					<NoResults
						trackingId="has-error"
						icon="gear"
						title={localize("_NotAbleToRetrieveReactions")}
						message={localize("_WeWereUnableToRetrieveReactions")}
						onRetry={forceReload}
						height="fill"
					/>
				);
			default:
		}

		return (
			<NoResults
				trackingId="has-no-results"
				icon="gear"
				title={localize("_NotAbleToRetrieveReactions")}
				message={localize("_WeWereUnableToRetrieveReactions")}
				height="fill"
			/>
		);
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy || loadingState === "loading"}
			height="fill"
			trackingId="reactions-dialog"
		>
			<Dialog.Header
				title={localize("_Reactions")}
				onClose={onClose}
			/>
			<Dialog.Body>
				{loadingState !== "error" && (
					<div className={styles.tabs}>
						<TabsNavigation<ReactionKind | undefined>
							value={activeReactionTab}
							onChange={setActiveReactionTab}
							items={reactionTabs}
						/>
					</div>
				)}
				<div
					className={styles.content}
					onScroll={scrollHandler}
					ref={containerRef}
				>
					{renderItems()}
				</div>
			</Dialog.Body>
			<Dialog.Footer />
		</Dialog>
	);
};

ReactionsDialog.displayName = "ReactionsDialog";

export type ReactionsDialogData = PartlyPartial<ReactionsDialogProps, "onClose">;

export function useReactionsDialog(): [
	element: ReactElement | null,
	open: (openData: ReactionsDialogData) => Promise<void>
] {
	return useOpenDialog<ReactionsDialogData>(
		(openData, onClose) => {
			const {
				onClose: onCloseFromOpen,
				...props
			} = openData;

			return (
				<ReactionsDialog
					{...props}
					onClose={onClose}
				/>
			);
		}
	);
}

function convertToReactionTabs(
	reactionCounts: readonly ReactionCounterModel[],
	statisticsFormatter: NumberFormatter
): readonly TabsNavigationItemData<ReactionKind | undefined>[] {
	const sortedReactionCounts: readonly ReactionCounterModel[] = [...reactionCounts].sort(
		(a, b) => nativeSortFunction(
			a.count,
			b.count,
			DataSourceSortDirection.Descending
		)
	);
	const totalReactionCounts: number = reactionCounts
		.reduce(
			(sum, a) => sum + a.count,
			0
		);

	const tabs: TabsNavigationItemData<ReactionKind | undefined>[] = [];

	if (reactionCounts.length > 1) {
		tabs.push({
			text: `${localize("_All")} ${statisticsFormatter(totalReactionCounts)}`,
			title: localize("_All"),
			value: undefined,
			trackingId: generateTrackingId("reaction-tab", `|all`)
		});
	}

	for (const reactionCount of sortedReactionCounts) {
		tabs.push({
			text: statisticsFormatter(reactionCount.count),
			title: ensureNotNil(
				reactionNameMap[reactionCount.kind]
			),
			icon: {
				kind: "svg",
				name: ensureNotNil(
					reactionIconMap[reactionCount.kind]
				)
			},
			value: reactionCount.kind,
			trackingId: generateTrackingId("reaction-tab", `|kind-${reactionCount.kind}`)
		});
	}

	return tabs;
}

type FilterState = {
	kind: ReactionKind | undefined;
	itemId: number;
};

type GlobalContext = {
	itemsVersion: number;
	getReactions: GetReactionsCallback;
};

const useReactionsDataSource = createRemoteDataSource<
	ReactionUserModel,
	FilterState,
	GlobalContext
>()
	.setConfiguration({
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake,
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<ReactionUserModel>> => {
				const {
					amount,
					skip,
					signal,
					filterState,
					globalContext
				} = options;

				return await globalContext.getReactions(
					filterState.itemId,
					amount,
					skip,
					filterState.kind,
					signal
				);
			}
		}
	);